/**
 * @file care-block.schema.ts
 * @description Blocos que compõem um checkpoint (e os campos de um lembrete).
 *
 * O bloco NÃO sabe se será renderizado como passo de wizard ou como bolha de
 * chat. Essa é a decisão que mantém a porta aberta para o `renderMode: CHAT`
 * virar evolução sem migração de dado (decisão de 29/08/2026: fase 1 é wizard).
 *
 * Campos de apresentação que existem hoje são os que o wizard usa:
 *   - `groupWithNext`: bloco de texto que divide a tela com a pergunta seguinte
 *     (as "aberturas fixas" das specs clínicas, que não merecem passo próprio).
 *   - `hiddenByDefault`: bloco condicional, revelado por `SHOW_BLOCK`.
 */

import { z } from 'zod';
import { CareActionSchema } from './care-action.schema';

export const CareBlockTypeEnum = z.enum([
  'TEXT',
  'SINGLE_CHOICE',
  'MULTI_CHOICE',
  'OPEN_TEXT',
  'SCALE',
  'DATE',
  'NUMBER',
  'FILE',
  'SELECT',
  'VIDEO',
]);
export type CareBlockType = z.infer<typeof CareBlockTypeEnum>;

/**
 * Opção de resposta. `scoreValue` alimenta variáveis calculadas por soma
 * (PHQ-9 0–27, IRSAG 20–80, EPDS 0–30). `internalNote` documenta a intenção
 * clínica da resposta e nunca é exibida à usuária — pedido explícito da
 * especificação, para revisão de conteúdo.
 *
 * `exclusive` marca opções que zeram as demais em múltipla escolha
 * ("Nada específico", "Não percebo nenhum sinal"). Sem isso, o time clínico
 * teria que resolver a exclusão com regra, e a UX ficaria inconsistente.
 */
export const CareOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  scoreValue: z.number().optional(),
  internalNote: z.string().optional(),
  exclusive: z.boolean().optional().default(false),
  actions: z.array(CareActionSchema).default([]),
});
export type CareOption = z.infer<typeof CareOptionSchema>;

/** Fonte de uma lista de seleção: estática (options) ou catálogo do app. */
export const CareSelectSourceEnum = z.enum(['STATIC', 'MEDICATIONS', 'EXAMS', 'VACCINES']);

const BaseBlockFields = {
  /** Estável entre versões — regras referenciam por este id. */
  id: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  required: z.boolean().optional().default(false),
  groupWithNext: z.boolean().optional().default(false),
  hiddenByDefault: z.boolean().optional().default(false),
  /** Nome de variável a gravar automaticamente com a resposta deste bloco. */
  bindsToVariable: z.string().optional(),
};

const TextBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('TEXT'),
  body: z.string(),
  imageUrl: z.string().nullable().optional(),
  /** Renderiza como acordeão fechado — padrão "Entenda mais sobre esse exame". */
  collapsible: z.boolean().optional().default(false),
});

const SingleChoiceBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('SINGLE_CHOICE'),
  options: z.array(CareOptionSchema).min(1),
});

const MultiChoiceBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('MULTI_CHOICE'),
  options: z.array(CareOptionSchema).min(1),
  minSelections: z.number().int().min(0).optional(),
  maxSelections: z.number().int().min(1).optional(),
});

const OpenTextBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('OPEN_TEXT'),
  placeholder: z.string().optional(),
  maxLength: z.number().int().positive().optional(),
});

const ScaleBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('SCALE'),
  min: z.number(),
  max: z.number(),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
  /** `BAR` é a barra "Pouco → Muito" usada nos checkpoints de bem-estar. */
  display: z.enum(['BAR', 'NUMBER', 'EMOJI']).default('BAR'),
});

const DateBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('DATE'),
  /** Limites relativos a hoje, em dias. Negativo = passado. */
  minOffsetDays: z.number().int().optional(),
  maxOffsetDays: z.number().int().optional(),
  /** Oferece a saída "Não lembro" — padrão da DUM na spec da tentante. */
  allowUnknown: z.boolean().optional().default(false),
  unknownLabel: z.string().optional(),
});

const NumberBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('NUMBER'),
  unit: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  decimals: z.number().int().min(0).max(3).optional(),
});

const FileBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('FILE'),
  acceptedTypes: z.array(z.string()).default(['image/*', 'application/pdf']),
});

const SelectBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('SELECT'),
  source: CareSelectSourceEnum.default('STATIC'),
  options: z.array(CareOptionSchema).default([]),
});

const VideoBlockSchema = z.object({
  ...BaseBlockFields,
  blockType: z.literal('VIDEO'),
  url: z.string(),
  transcript: z.string().nullable().optional(),
});

export const CareBlockSchema = z.discriminatedUnion('blockType', [
  TextBlockSchema,
  SingleChoiceBlockSchema,
  MultiChoiceBlockSchema,
  OpenTextBlockSchema,
  ScaleBlockSchema,
  DateBlockSchema,
  NumberBlockSchema,
  FileBlockSchema,
  SelectBlockSchema,
  VideoBlockSchema,
]);

export type CareBlock = z.infer<typeof CareBlockSchema>;

/** Blocos que aceitam resposta (todos menos os puramente expositivos). */
export const ANSWERABLE_BLOCK_TYPES: CareBlockType[] = [
  'SINGLE_CHOICE',
  'MULTI_CHOICE',
  'OPEN_TEXT',
  'SCALE',
  'DATE',
  'NUMBER',
  'FILE',
  'SELECT',
];

export function isAnswerableBlock(block: CareBlock): boolean {
  return ANSWERABLE_BLOCK_TYPES.includes(block.blockType);
}

export {
  TextBlockSchema,
  SingleChoiceBlockSchema,
  MultiChoiceBlockSchema,
  OpenTextBlockSchema,
  ScaleBlockSchema,
  DateBlockSchema,
  NumberBlockSchema,
  FileBlockSchema,
  SelectBlockSchema,
  VideoBlockSchema,
};
